/**
 * Who this browser is.
 *
 * Shared because every application Scout serves has to answer the same
 * question, and answering it twice is how two applications come to disagree
 * about whether somebody is signed in.
 *
 * What is deliberately not here is signing up. `/signup` establishes a buyer
 * and claims the anonymous brief this browser was holding — a story that
 * belongs to the product and that an operator's console does not share. An
 * application whose sign-in means something different keeps its own.
 */
import { useQuery } from '@tanstack/react-query'

import { ApiError, request } from './http'

export interface Person {
  id: string
  buyer_id: string
  /** What the boundary attests, and the whole of what Scout knows about who they are. */
  identity: { subject: string; email: string }
  /** What they call themselves. Empty until given; nothing is invented for it. */
  name: string
  /**
   * What their organisation calls itself. Empty until given, like their own.
   *
   * Carried on the person rather than fetched separately, so the account area
   * has both facts at once and does not redraw itself as the second arrives.
   * It is the buyer's name, not the person's, and the two are asked for
   * together when a brief is signed up.
   */
  buyer_name: string
  created_at: string
  updated_at: string
}

export const identityKeys = {
  /** Who this browser is. One key, because there is only ever one answer. */
  me: ['me'] as const,
}

/**
 * Who this browser is, or nobody.
 *
 * A 401 is the answer "nobody is signed in", not a failure: it settles to
 * `null` rather than throwing, so every caller can ask `who.person === null`
 * instead of picking apart an error. `retry: false` for the same reason a 404
 * on the held brief is not retried — being nobody is a stable fact, and asking
 * again will not change it.
 *
 * Nothing else may stand in for this. The temptation is to treat the result of
 * signing up as identity, but that is one component's mutation result and it
 * does not survive a reload.
 */
export function useMe() {
  const query = useQuery({
    queryKey: identityKeys.me,
    queryFn: async (): Promise<Person | null> => {
      try {
        return await request<Person>('/me')
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null
        throw error
      }
    },
    retry: false,
  })

  return {
    /** The person this browser is, or null for nobody. Null while unsettled. */
    person: query.data ?? null,
    /**
     * Whether the question has been answered yet.
     *
     * Callers that would otherwise guess must wait on this. A brief never
     * changes owner, so starting the wrong kind of brief for someone is not a
     * mistake that can be corrected afterwards.
     */
    settled: !query.isPending,
    /**
     * A failed read settles to nobody rather than to an error. That is the
     * path an anonymous visitor takes anyway, and the one that works with no
     * boundary configured at all — so an identity service having a bad moment
     * costs the sidebar, never the way in.
     */
    failed: query.isError,
  }
}

/**
 * Stops this browser presenting a session to Scout.
 *
 * The boundary's own logout ends the session there; this is what makes the
 * browser stop being authenticated *here*. The two are not the same thing, and
 * doing only the first is why signing out appeared to do nothing: Scout
 * verifies sessions locally against a cached key set, so a session revoked at
 * the boundary goes on being accepted until it expires.
 */
export function signOutOfScout(): Promise<null> {
  return request<null>('/signout', { method: 'POST' })
}
